import type { QuestionDefinition } from "@reno-estimator/core";

/**
 * Garage's full question set (Phase 13B) - see rules/garage.ts for the
 * matching calculator. Size uses the existing generic `room_size`
 * question/slider (packages/config/src/questions/common-questions.ts),
 * same as deck/patio (Phase 11) - the brief didn't ask for a dedicated
 * bucketed car-bay-count step here, so this phase reuses the generic
 * "approximately how large is the space" slider rather than inventing
 * one.
 */
export const garageQuestions: QuestionDefinition[] = [
  {
    id: "garage_attachment",
    projectTypes: ["garage"],
    inputType: "single_select",
    prompt: "Is the garage attached or detached?",
    helpText: "This affects electrical (a detached garage usually needs its own service run) and fire-separation assumptions (an attached garage shares structure with the house).",
    required: true,
    options: [
      { id: "attached", label: "Attached", description: "Shares a wall/structure with the house." },
      { id: "detached", label: "Detached", description: "A separate structure." }
    ],
    order: 180
  },
  {
    id: "garage_flooring",
    projectTypes: ["garage"],
    inputType: "single_select",
    prompt: "What's happening with the garage floor?",
    required: false,
    options: [
      { id: "none", label: "No change" },
      { id: "basic_coating", label: "Basic floor coating", description: "A basic sealer or paint-on coating." },
      { id: "epoxy", label: "Epoxy coating", description: "A durable, professional-grade epoxy floor system." }
    ],
    order: 190
  },
  {
    id: "garage_insulation",
    projectTypes: ["garage"],
    inputType: "single_select",
    prompt: "What insulation upgrade, if any?",
    required: false,
    options: [
      { id: "none", label: "No change" },
      { id: "walls_only", label: "Walls only" },
      { id: "walls_and_ceiling", label: "Walls and ceiling" }
    ],
    order: 200
  },
  {
    id: "garage_drywall",
    projectTypes: ["garage"],
    inputType: "single_select",
    prompt: "What's happening with drywall?",
    required: false,
    options: [
      { id: "none", label: "No change" },
      { id: "basic", label: "Basic drywall", description: "Hung and taped, not painted." },
      { id: "finished_painted", label: "Finished and painted" }
    ],
    order: 210
  },
  {
    id: "garage_door_scope",
    projectTypes: ["garage"],
    inputType: "single_select",
    prompt: "Are you replacing the garage door(s)?",
    helpText: "The garage door is usually one of the largest single line items in a garage renovation.",
    required: false,
    options: [
      { id: "keep_existing", label: "Keeping existing door(s)" },
      { id: "single_standard", label: "Single door - standard", description: "One standard (non-insulated) garage door." },
      { id: "single_insulated", label: "Single door - insulated" },
      { id: "double_standard", label: "Double door - standard", description: "One double-width (or two single) standard door(s)." },
      { id: "double_insulated", label: "Double door - insulated" }
    ],
    order: 220
  },
  {
    id: "garage_electrical_upgrade",
    projectTypes: ["garage"],
    inputType: "single_select",
    prompt: "Any garage-specific electrical upgrades?",
    helpText: "Beyond general electrical changes - e.g. a subpanel/240V circuit for EV charging, or dedicated workshop circuits.",
    required: false,
    options: [
      { id: "none", label: "No upgrade" },
      { id: "subpanel_240v", label: "Subpanel / 240V circuit", description: "For EV charging or a single high-draw appliance." },
      { id: "workshop_circuits", label: "Dedicated workshop circuits", description: "Multiple dedicated circuits for tools/equipment." }
    ],
    order: 230
  },
  {
    id: "garage_storage",
    projectTypes: ["garage"],
    inputType: "single_select",
    prompt: "Any storage or organization systems?",
    required: false,
    options: [
      { id: "none", label: "None" },
      { id: "basic_shelving", label: "Basic shelving" },
      { id: "custom_system", label: "Custom storage system", description: "Wall-mounted cabinetry, slat-wall, overhead racks, etc." }
    ],
    order: 240
  },
  {
    id: "garage_heating",
    projectTypes: ["garage"],
    inputType: "single_select",
    prompt: "Is the garage being heated?",
    required: false,
    options: [
      { id: "none", label: "No heating" },
      { id: "electric_baseboard", label: "Electric baseboard" },
      { id: "radiant_floor", label: "Radiant in-floor heating" },
      { id: "gas_forced_air", label: "Gas forced-air", description: "Requires a gas line and permit - see the note on your results." }
    ],
    order: 250
  }
];
